import { plainWeaveShaderWGSL } from './plainWeave.code'
import type {
    PlainWeaveParams,
    PlainWeaveResources,
    RenderPlainWeaveParams,
    TextureExportConfig
} from "./plainWeave.types";
import {
    HTML标签_CANVAS,
    CANVAS上下文_2D,
    错误消息_无法获取2D上下文,
    错误消息_纹理转Blob失败,
    MIME类型_PNG,
    GPU缓冲区对齐,
    每像素字节数
} from "./plainWeave.constants";

export function createGradientTexture(device: GPUDevice, stops: { offset: number, color: string }[]): GPUTexture {
    const width = 256;
    const height = 1;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2d context');

    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    const sortedStops = [...stops].sort((stopA, stopB) => stopA.offset - stopB.offset);

    for (const stop of sortedStops) {
        gradient.addColorStop(stop.offset, stop.color);
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    const imageData = ctx.getImageData(0, 0, width, height);

    const texture = device.createTexture({
        size: [width, height],
        format: 'rgba8unorm',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    });

    device.queue.writeTexture(
        { texture },
        imageData.data,
        { bytesPerRow: width * 4 },
        [width, height]
    );

    return texture;
}

export function createPlainWeaveUniformBuffer(device: GPUDevice, params: PlainWeaveParams): GPUBuffer {
    const uniformData = new Float32Array(44);

    // Identity Matrix
    uniformData[0] = 1; uniformData[5] = 1; uniformData[10] = 1; uniformData[15] = 1;

    // 核心参数 (16-19)
    uniformData[16] = params.tileSize;
    uniformData[17] = params.threadDensity;
    uniformData[18] = params.threadThickness;
    uniformData[19] = params.warpWeftRatio;

    // 纱线结构 (20-22)
    uniformData[20] = params.threadTwist;
    uniformData[21] = params.fiberDetail;
    uniformData[22] = params.fuzziness;

    // 织造特征 (23-25)
    uniformData[23] = params.weaveTightness;
    uniformData[24] = params.threadUnevenness;
    uniformData[25] = params.weaveImperfection;

    uniformData[26] = 0; // padding1

    // 高级参数 (27-30)
    uniformData[27] = params.fbmOctaves;
    uniformData[28] = params.fbmAmplitude;
    uniformData[29] = params.noiseFrequency;
    uniformData[30] = params.colorVariation;

    // 光泽和材质 (31-35)
    uniformData[31] = params.warpSheen;
    uniformData[32] = params.weftSheen;
    uniformData[33] = params.roughnessMin;
    uniformData[34] = params.roughnessMax;
    uniformData[35] = params.normalStrength;

    // 纱线厚度调节 (36-37)
    uniformData[36] = params.threadHeightScale;
    uniformData[37] = params.threadShadowStrength;

    const uniformBuffer = device.createBuffer({
        size: uniformData.byteLength,
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(uniformBuffer, 0, uniformData);

    return uniformBuffer;
}

export function createPlainWeavePipeline(device: GPUDevice): GPURenderPipeline {
    const module = device.createShaderModule({
        code: plainWeaveShaderWGSL,
    });

    return device.createRenderPipeline({
        layout: 'auto',
        vertex: {
            module,
            entryPoint: 'vs_main',
            buffers: [
                {
                    arrayStride: 20, // 3 pos + 2 uv = 5 floats * 4 bytes
                    attributes: [
                        { shaderLocation: 0, offset: 0, format: 'float32x3' },
                        { shaderLocation: 1, offset: 12, format: 'float32x2' },
                    ],
                },
            ],
        },
        fragment: {
            module,
            entryPoint: 'fs_main',
            targets: [{ format: 'bgra8unorm' }],
        },
        primitive: {
            topology: 'triangle-list',
        },
    });
}



export function createPlainWeaveResources(device: GPUDevice, params: PlainWeaveParams): PlainWeaveResources {
    const uniformBuffer = createPlainWeaveUniformBuffer(device, params);
    const gradientTexture = createGradientTexture(device, params.gradientStops);
    const sampler = device.createSampler({
        magFilter: 'linear',
        minFilter: 'linear',
        addressModeU: 'clamp-to-edge',
        addressModeV: 'clamp-to-edge',
    });

    const pipeline = createPlainWeavePipeline(device);

    const bindGroup = device.createBindGroup({
        layout: pipeline.getBindGroupLayout(0),
        entries: [
            { binding: 0, resource: { buffer: uniformBuffer } },
            { binding: 1, resource: gradientTexture.createView() },
            { binding: 2, resource: sampler },
        ],
    });

    return { pipeline, bindGroup };
}

export function createFullscreenQuadBuffer(device: GPUDevice): GPUBuffer {
    // x, y, z, u, v
    const vertices = new Float32Array([
        -1, -1, 0, 0, 1,
        1, -1, 0, 1, 1,
        -1, 1, 0, 0, 0,
        -1, 1, 0, 0, 0,
        1, -1, 0, 1, 1,
        1, 1, 0, 1, 0,
    ]);
    const vertexBuffer = device.createBuffer({
        size: vertices.byteLength,
        usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(vertexBuffer, 0, vertices);
    return vertexBuffer;
}



export function runPlainWeaveRenderPass(device: GPUDevice, params: RenderPlainWeaveParams): void {
    const { texture, pipeline, bindGroup, vertexBuffer } = params;
    const commandEncoder = device.createCommandEncoder();
    const passEncoder = commandEncoder.beginRenderPass({
        colorAttachments: [
            {
                view: texture.createView(),
                loadOp: 'clear',
                clearValue: { r: 0, g: 0, b: 0, a: 1 },
                storeOp: 'store',
            },
        ],
    });
    passEncoder.setPipeline(pipeline);
    passEncoder.setBindGroup(0, bindGroup);
    passEncoder.setVertexBuffer(0, vertexBuffer);
    passEncoder.draw(6, 1, 0, 0);
    passEncoder.end();

    device.queue.submit([commandEncoder.finish()]);
}



/**
 * 从 GPU 纹理读取像素数据到 Canvas
 *
 * 这是一个内部辅助函数，用于将 GPU 纹理数据复制到 Canvas 上下文
 * 处理 BGRA 到 RGBA 的颜色通道转换和行对齐
 *
 * @param config - 纹理导出配置
 * @returns Canvas 元素，包含纹理数据
 */
async function readTextureToCanvas(config: TextureExportConfig): Promise<HTMLCanvasElement> {
    const { device, texture, width, height } = config;
    const unalignedBytesPerRow = width * 每像素字节数;
    const bytesPerRow = Math.ceil(unalignedBytesPerRow / GPU缓冲区对齐) * GPU缓冲区对齐;

    const readBuffer = device.createBuffer({
        size: bytesPerRow * height,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });

    const commandEncoder = device.createCommandEncoder();
    commandEncoder.copyTextureToBuffer(
        { texture },
        { buffer: readBuffer, bytesPerRow },
        [width, height]
    );

    device.queue.submit([commandEncoder.finish()]);

    await readBuffer.mapAsync(GPUMapMode.READ);
    const arrayBuffer = readBuffer.getMappedRange();

    const canvas = document.createElement(HTML标签_CANVAS);
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext(CANVAS上下文_2D);
    if (!ctx) throw new Error(错误消息_无法获取2D上下文);

    const data = new Uint8Array(arrayBuffer);
    const imageData = ctx.createImageData(width, height);

    for (let y = 0; y < height; y++) {
        const srcOffset = y * bytesPerRow;
        const dstOffset = y * width * 4;

        for (let x = 0; x < width; x++) {
            const srcIndex = srcOffset + x * 4;
            const dstIndex = dstOffset + x * 4;
            // BGRA -> RGBA 转换
            imageData.data[dstIndex] = data[srcIndex + 2];     // R
            imageData.data[dstIndex + 1] = data[srcIndex + 1]; // G
            imageData.data[dstIndex + 2] = data[srcIndex];     // B
            imageData.data[dstIndex + 3] = data[srcIndex + 3]; // A
        }
    }

    ctx.putImageData(imageData, 0, 0);
    readBuffer.unmap();

    return canvas;
}

/**
 * @简洁函数 向后兼容的便捷函数
 * @deprecated 请使用 convertTextureToBlob 替代，性能更好
 * 将 GPU 纹理转换为 Base64 编码的 DataURL
 *
 * 注意：此函数使用 toDataURL，会导致：
 * - Base64 编码开销（体积膨胀 33%）
 * - 阻塞主线程
 * - 内存占用翻倍
 *
 * @param config - 纹理导出配置
 * @returns Base64 编码的 PNG DataURL
 */
export async function convertTextureToBase64(config: TextureExportConfig): Promise<string> {
    const canvas = await readTextureToCanvas(config);
    return canvas.toDataURL(MIME类型_PNG);
}

/**
 * 将 GPU 纹理转换为 Blob 对象
 *
 * 使用 toBlob 替代 toDataURL，性能提升约 2.7 倍：
 * - 异步操作，不阻塞主线程
 * - 直接生成二进制数据，零编码开销
 * - 内存占用更低
 *
 * @param config - 纹理导出配置
 * @returns PNG 格式的 Blob 对象
 */
export async function convertTextureToBlob(config: TextureExportConfig): Promise<Blob> {
    const canvas = await readTextureToCanvas(config);
    
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                    return;
                }
                reject(new Error(错误消息_纹理转Blob失败));
            },
            MIME类型_PNG
        );
    });
}

/**
 * @简洁函数 便捷函数，组合 convertTextureToBlob 和 URL.createObjectURL
 * 将 GPU 纹理转换为 Blob URL
 *
 * 这是最推荐的纹理导出方式，返回可直接用于显示或下载的 URL
 *
 * 注意：调用方需要在不再使用时调用 URL.revokeObjectURL() 释放内存
 *
 * @param config - 纹理导出配置
 * @returns Blob URL 字符串
 */
export async function convertTextureToBlobUrl(config: TextureExportConfig): Promise<string> {
    const blob = await convertTextureToBlob(config);
    return URL.createObjectURL(blob);
}
